import React, { useEffect, useState, useMemo } from 'react';
import '../index.css';
import { useOutletContext, useParams } from "react-router-dom";
import ProfileStream from '../ProfileStream';
import Header from '../Header';
import { getCityProfiles } from "../Cities";
import ElectionCycleDropdown from '../ElectionCycleDropdown';
import CumulativeContributionsTimeline from '../CumulativeContributionsTimeline';
import PACFundingBarChart from '../PACFundingBarChart';
import FinancialRecordsTable from '../FinancialRecordsTable';
import CrossCampaignDonors from '../CrossCampaignDonors';
import NearLimitFundingChart from '../NearLimitFundingChart';

const generateElectionCycles = (profiles) => {
    console.log(profiles)
    const earliestFirstElection = Math.min(
        ...profiles.map(profile => parseInt(profile.first_election, 10))
    );
    const latestNextElection = Math.max(
        ...profiles.map(profile => parseInt(profile.next_election, 10))
    );

    const smallestCycleYears = Math.min(
        ...profiles.map(profile => profile.election_cycle_years)
    );

    const electionCycles = [];
    let year = earliestFirstElection - smallestCycleYears;
    const [month, day] = profiles[0].election_date.split('-').map(Number);
    const election_cycle_years = profiles[0].election_cycle_years;

    while (year < latestNextElection) {
        const startDate = new Date(year, month - 1, day);
        const endDate = new Date(year + election_cycle_years, month - 1, day - 1);
        electionCycles.push({
            start: startDate,
            end: endDate,
        });
        year += election_cycle_years;
    }

    return electionCycles;
};

function City() {
    const { cityId } = useParams();
    const { city_config } = useOutletContext();

    const [cityProfileData, setCityProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDateRange, setSelectedDateRange] = useState(null);

    // Fetch city profile data
    useEffect(() => {
        const fetchCityData = async () => {
            try {
                const data = await getCityProfiles(cityId);
                setCityProfileData(data);
            } catch (err) {
                setError("Failed to load city profile data.");
            } finally {
                setLoading(false);
            }
        };

        fetchCityData();
    }, [cityId]);

    // Generate election cycles using useMemo
    const electionCycles = useMemo(() => {
        return cityProfileData ? generateElectionCycles(cityProfileData) : [];
    }, [cityProfileData]);

    // Set selected date range once election cycles are available
    useEffect(() => {
        if (electionCycles.length > 0) {
            setSelectedDateRange({
                start: electionCycles[0].start,
                end: electionCycles[electionCycles.length - 1].end,
            });
        }
    }, [electionCycles]);

    const flattenSchedule = (profiles, scheduleName) =>
        (profiles ?? []).flatMap(profile =>
            (profile[scheduleName] ?? []).map(record => ({
                ...record,
                Campaign: profile.name,
                campaign_id: profile.id,
            }))
        );


    const citySchedules = useMemo(() => ({
        contributions: flattenSchedule(cityProfileData, "contributions"),
        expenditures: flattenSchedule(cityProfileData, "expenditures"),
        loans: flattenSchedule(cityProfileData, "loans"),
        in_kind_contributions: flattenSchedule(cityProfileData, "in_kind_contributions"),
        credit_card_expenditures: flattenSchedule(cityProfileData, "credit_card_expenditures"),
        investment_purchases: flattenSchedule(cityProfileData, "investment_purchases"),
        personal_funds_expenditures: flattenSchedule(cityProfileData, "personal_funds_expenditures"),
        interest_gained: flattenSchedule(cityProfileData, "interest_gained"),
        non_political_expenditures_made_from_political_contributions: flattenSchedule(cityProfileData, "non_political_expenditures_made_from_political_contributions"),
        payments_to_candidate_business: flattenSchedule(cityProfileData, "payments_to_candidate_business"),
        pledged_contributions: flattenSchedule(cityProfileData, "pledged_contributions"),
        unpaid_incurred_obligations: flattenSchedule(cityProfileData, "unpaid_incurred_obligations"),
    }), [cityProfileData]);
          

    // Handle loading and error states
    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;
    if (!electionCycles.length || !selectedDateRange) {
        return <div>Preparing data...</div>;
    }
    return (
        <div>
            <div id="city-header">
                <Header city={city_config.name} profile={city_config} />
                
                <ElectionCycleDropdown
                    electionCycles={electionCycles}
                    selectedDateRange={selectedDateRange}
                    setSelectedDateRange={setSelectedDateRange}
                />
            </div>
            
            <CumulativeContributionsTimeline
                cityProfileData={cityProfileData}
                selectedDateRange={selectedDateRange} />

            <PACFundingBarChart 
                allContributions={citySchedules.contributions} />

            <NearLimitFundingChart
                contributions={citySchedules.contributions}
                selectedDateRange={selectedDateRange}
            />

            <CrossCampaignDonors
                contributions={citySchedules.contributions}
                selectedDateRange={selectedDateRange}
            />

            <ProfileStream 
                cityId={cityId}
                cityProfileData={cityProfileData} />

            <FinancialRecordsTable
                profile={cityProfileData?.[0]?.yaml_profile}
                selectedDateRange={selectedDateRange}
                schedules={citySchedules}
            />
        </div>
    );
}

export default City;
